import React, { useState, useEffect, useRef } from "react";

export default function FeedbackPopup({
  isOpen,
  onClose = () => {},
  initialEmail = "",
  onSubmit, // optional async function({ message, email })
}) {
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState(initialEmail || "");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const textareaRef = useRef(null);
  const closeTimerRef = useRef(null);

  // prevent background scroll when popup is open
  const prevBodyStyleRef = useRef({
    overflow: "",
    position: "",
    width: "",
    paddingRight: ""
  });

  useEffect(() => {
    if (isOpen) {
      const body = document.body;
      // store previous styles
      prevBodyStyleRef.current = {
        overflow: body.style.overflow || "",
        position: body.style.position || "",
        width: body.style.width || "",
        paddingRight: body.style.paddingRight || ""
      };
      // compensate for scrollbar to avoid layout shift
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      if (scrollbarWidth > 0) {
        body.style.paddingRight = `${scrollbarWidth}px`;
      }
      body.style.overflow = "hidden";
      body.style.position = "fixed";
      body.style.width = "100%";
    } else {
      // restore previous styles
      const prev = prevBodyStyleRef.current;
      document.body.style.overflow = prev.overflow;
      document.body.style.position = prev.position;
      document.body.style.width = prev.width;
      document.body.style.paddingRight = prev.paddingRight;
    }

    // cleanup on unmount as well
    return () => {
      const prev = prevBodyStyleRef.current;
      document.body.style.overflow = prev.overflow;
      document.body.style.position = prev.position;
      document.body.style.width = prev.width;
      document.body.style.paddingRight = prev.paddingRight;
    };
  }, [isOpen]);

  // keep email in sync if initialEmail changes while closed/open
  useEffect(() => {
    setEmail(initialEmail || "");
  }, [initialEmail]);

  // focus textarea when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => textareaRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // ESC key closes popup
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && isOpen) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  // clear timers on unmount
  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
    };
  }, []);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const trimmed = message.trim();
    if (!trimmed || submitting) return;

    setSubmitting(true);
    try {
      if (onSubmit && typeof onSubmit === "function") {
        await onSubmit({ message: trimmed, email: email.trim() });
      } else {
        // simulated network delay when no handler is provided
        await new Promise((res) => setTimeout(res, 700));
      }
      setSubmitted(true);
      // show "Submitted" briefly, then close and reset
      closeTimerRef.current = setTimeout(() => {
        closeTimerRef.current = null;
        setSubmitting(false);
        setSubmitted(false);
        setMessage("");
        onClose();
      }, 900);
    } catch (err) {
      // keep simple: stop submitting and leave message for retry
      setSubmitting(false);
      setSubmitted(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50"
      onClick={() => onClose()}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white rounded-2xl shadow-lg w-full max-w-lg p-6 mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-lg font-semibold">Send Feedback</h3>
          <button
            onClick={() => onClose()}
            aria-label="Close feedback"
            className="text-gray-500 hover:text-gray-800"
            type="button"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <label className="text-sm text-gray-700">Your feedback</label>
          <textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={6}
            placeholder="Tell us what's working, what's not, or any ideas..."
            className="w-full p-3 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none text-sm"
            required
          />

          <div className="flex items-center justify-between mt-2">
            <p className="text-xs text-gray-500">{message.trim().length} characters</p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  setMessage("");
                }}
                className="px-3 py-2 rounded-md bg-gray-100 text-sm hover:bg-gray-200"
              >
                Clear
              </button>
              <button
                type="submit"
                disabled={submitting || !message.trim()}
                className={`px-4 py-2 rounded-md text-sm ${
                  submitting || !message.trim()
                    ? "bg-gray-900 text-white cursor-not-allowed"
                    : submitted || submitting
                      ? "bg-black text-black"
                      : "bg-black text-white hover:bg-gray-900"
                }`}
              >
                {submitting ? "Submitting..." : submitted ? "Submitted" : "Submit"}
              </button>
            </div>
          </div>
        </form>

        {/* optional small hint */}
        <p className="text-xs text-gray-400 mt-3">
          We'll use your feedback to improve 1xSkills. Thank you!
        </p>
      </div>
    </div>
  );
}